/**
 * DEPLETIONS-INVENTORY MODULE
 * Self-contained. Import only from /shared or within this module.
 *
 * dashboardSummary — this module's contribution to the operations-hub (Home).
 * Computes two real KPIs from existing data and a low-stock "Needs you" signal:
 *   - Days on Hand at Distributor = distributor on-hand cases ÷ avg daily
 *       depletion rate (a future "Days of Supply" KPI will measure OUR own
 *       warehouse stock — distinct metric, different source).
 *   - Depletions = case sales, toggleable YTD / This Month / Last Month (YTD
 *       carries a YoY delta vs the same window last year).
 * No forecasts/door-level data required (those KPIs come later, per module).
 */

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

import { loadInventory, loadAvgMonthlySales, loadDepletions } from "./depletionService.js";

const DAYS_PER_MONTH = 30.44;

export async function getDashboardSummary() {
  const kpis = [];
  const signals = [];
  const now = new Date();
  const year = now.getFullYear();

  const [invRes, avgRes, curRes, prevRes] = await Promise.allSettled([
    loadInventory(),
    loadAvgMonthlySales(3),
    loadDepletions({ year }),
    loadDepletions({ year: year - 1 }),
  ]);

  const inventory = invRes.status === "fulfilled" ? invRes.value : [];
  const avgMap    = avgRes.status === "fulfilled" ? avgRes.value : {};
  const cur       = curRes.status === "fulfilled" ? curRes.value : [];
  const prev      = prevRes.status === "fulfilled" ? prevRes.value : [];

  // ── Days on Hand at Distributor ──
  const onHand  = inventory.reduce((s, r) => s + (Number(r.current_inventory) || 0), 0);
  const monthly = Object.values(avgMap).reduce((s, v) => s + (Number(v) || 0), 0);
  const daily   = monthly / DAYS_PER_MONTH;
  if (onHand > 0 && daily > 0) {
    const doh = Math.round(onHand / daily);
    const status = doh < 14 ? "critical" : doh < 30 ? "warn" : "healthy";
    kpis.push({
      key: "days-on-hand-distributor", label: "Days on Hand at Distributor", display: String(doh), unit: "days",
      sub: status === "healthy" ? "Healthy band" : status === "warn" ? "Getting low" : "Reorder now",
      status, to: "/inventory",
    });
    if (doh < 30) {
      signals.push({
        key: "low-days-on-hand", severity: doh < 14 ? "critical" : "warn",
        title: `Distributor days on hand is low — ${doh} days`,
        detail: "Plan a production run or reorder before they stock out.",
        to: "/inventory", actionLabel: "Inventory",
      });
    }
  }

  // ── Depletions (toggle: YTD / This Month / Last Month) ──
  const curMonth = now.getMonth() + 1;
  const maxMonth = cur.reduce((m, r) => Math.max(m, Number(r.month) || 0), 0);
  const sumThrough = (rows, m) =>
    rows.reduce((s, r) => s + ((Number(r.month) || 0) <= m ? (Number(r.case_sales) || 0) : 0), 0);
  const sumMonth = (rows, m) =>
    rows.reduce((s, r) => s + ((Number(r.month) || 0) === m ? (Number(r.case_sales) || 0) : 0), 0);

  const ytd     = sumThrough(cur, maxMonth);
  const lastYtd = sumThrough(prev, maxMonth);
  if (ytd > 0) {
    let ytdSub = "cases this year", ytdStatus = "neutral";
    if (lastYtd > 0) {
      const pct = ((ytd - lastYtd) / lastYtd) * 100;
      ytdSub = `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}% vs last year`;
      ytdStatus = pct >= 0 ? "healthy" : "warn";
    }
    const thisMonth = sumMonth(cur, curMonth);
    const lastMonthNum = curMonth > 1 ? curMonth - 1 : 12;
    const lastMonthVal = curMonth > 1 ? sumMonth(cur, lastMonthNum) : sumMonth(prev, 12);

    kpis.push({
      key: "depletions", label: "Depletions", to: "/depletions",
      storageKey: "bc.dash.depletions", defaultVariant: "ytd",
      variants: [
        { key: "ytd",  label: "YTD",     display: Math.round(ytd).toLocaleString(),         unit: "cases", sub: ytdSub, status: ytdStatus },
        { key: "mtd",  label: "This Mo", display: Math.round(thisMonth).toLocaleString(),    unit: "cases", sub: `${MONTH_NAMES[curMonth - 1]} so far`, status: "neutral" },
        { key: "last", label: "Last Mo", display: Math.round(lastMonthVal).toLocaleString(), unit: "cases", sub: `${MONTH_NAMES[lastMonthNum - 1]} total`, status: "neutral" },
      ],
    });
  }

  // ── Depletions Month over Month ── (anchored to TODAY's calendar month, not
  // the latest month that happens to have data — the current month is partial,
  // so this reads as month-to-date vs the prior full month). Renders alongside
  // the Depletions tile whenever there's current-year data; when this month has
  // no depletions yet it shows 0% rather than disappearing.
  if (ytd > 0) {
    const priorMonthNum = curMonth > 1 ? curMonth - 1 : 12;
    const currentVal = sumMonth(cur, curMonth);
    const priorVal   = curMonth > 1 ? sumMonth(cur, priorMonthNum) : sumMonth(prev, 12);
    const computable = currentVal > 0 && priorVal > 0;
    const pct = computable ? ((currentVal - priorVal) / priorVal) * 100 : 0;
    kpis.push({
      key: "depletions-mom", label: "Depletions MoM",
      display: `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`,
      sub: computable
        ? `${MONTH_NAMES[curMonth - 1]} vs ${MONTH_NAMES[priorMonthNum - 1]}`
        : `No ${MONTH_NAMES[curMonth - 1]} depletions yet`,
      status: computable ? (pct >= 0 ? "healthy" : "warn") : "neutral",
      to: "/depletions",
    });
  }

  return { kpis, signals };
}
